import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Paperclip, Loader2, CheckCircle2, FileText, PaperclipIcon, Eye } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { getAttachmentOpenUrl } from "@/lib/attachmentDownload";

/**
 * Dialog anteprima email "Polizza pronta".
 * Mostra oggetto, corpo (editabile), lista allegati visibili al cliente con
 * selezione per allegato + toggle "invia allegati via email" e riepilogo finale.
 * Aperto da Policies.jsx quando una polizza passa da `in_emissione` → stato finale.
 */
export function PolicyReadyNoticeDialog({ open, onClose, policy }) {
  const [atts, setAtts] = useState([]);
  const [loadingAtts, setLoadingAtts] = useState(false);
  const [extraMessage, setExtraMessage] = useState("");
  const [sending, setSending] = useState(false);
  // 🆕 Feb 2026 — Selezione allegati e toggle invio allegati via email
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [includeAttachments, setIncludeAttachments] = useState(true);
  // 🆕 Feb 2026 — Preferenze ricorrenti per compagnia
  const [rememberPrefs, setRememberPrefs] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [previewingId, setPreviewingId] = useState(null);

  useEffect(() => {
    if (!open || !policy?.id) return;
    let cancelled = false;
    (async () => {
      setLoadingAtts(true);
      setPrefsLoaded(false);
      try {
        const [attsResp, prefsResp] = await Promise.all([
          api.get(`/policies/${policy.id}/attachments`),
          policy.company_id
            ? api.get(`/companies/${policy.company_id}/ready-notice-preferences`).catch(() => ({ data: { included_filenames: [] } }))
            : Promise.resolve({ data: { included_filenames: [] } }),
        ]);
        if (cancelled) return;
        const all = Array.isArray(attsResp.data) ? attsResp.data : (attsResp.data?.items || []);
        const visible = all.filter((a) => a.client_visible !== false && !a.is_deleted);
        setAtts(visible);
        const included = new Set((prefsResp.data?.included_filenames || []).map((s) => String(s).toLowerCase().trim()));
        if (included.size > 0) {
          // Preferenze esistono: pre-check solo file con signature matching
          const sig = (a) => String(a.original_filename || a.filename || "").toLowerCase().trim();
          setSelectedIds(new Set(visible.filter((a) => included.has(sig(a))).map((a) => a.id)));
          setRememberPrefs(true);
        } else {
          // Default: tutti selezionati
          setSelectedIds(new Set(visible.map((a) => a.id)));
          setRememberPrefs(false);
        }
        setPrefsLoaded(true);
      } catch {
        if (!cancelled) { setAtts([]); setSelectedIds(new Set()); setPrefsLoaded(true); }
      } finally {
        if (!cancelled) setLoadingAtts(false);
      }
    })();
    setExtraMessage("");
    setIncludeAttachments(true);
    return () => { cancelled = true; };
  }, [open, policy?.id, policy?.company_id]);

  const toggleAtt = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(atts.map((a) => a.id)));
  const deselectAll = () => setSelectedIds(new Set());

  const previewAtt = async (a) => {
    setPreviewingId(a.id);
    try {
      const { url, revoke } = await getAttachmentOpenUrl(a.id, { contentType: a.content_type });
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.warning("Popup bloccato dal browser — abilita i popup per l'anteprima");
      if (revoke) setTimeout(revoke, 10000);
    } catch (e) {
      toast.error(`Anteprima non disponibile: ${formatApiError(e)}`);
    } finally {
      setPreviewingId(null);
    }
  };

  const send = async () => {
    if (!policy?.id) return;
    setSending(true);
    try {
      const attachmentIds = includeAttachments ? Array.from(selectedIds) : [];
      const body = {
        extra_message: extraMessage.trim() || null,
        include_attachments: includeAttachments,
        attachment_ids: attachmentIds,
      };
      await api.post(`/policies/${policy.id}/send-ready-notice`, body);
      // 🆕 Salva preferenze compagnia se opt-in
      if (rememberPrefs && policy.company_id) {
        const sigOf = (id) => {
          const a = atts.find((x) => x.id === id);
          return String(a?.original_filename || a?.filename || "").toLowerCase().trim();
        };
        const includedFilenames = includeAttachments
          ? Array.from(new Set(attachmentIds.map(sigOf).filter(Boolean)))
          : [];
        try {
          await api.put(`/companies/${policy.company_id}/ready-notice-preferences`, {
            included_filenames: includedFilenames,
          });
        } catch { /* ignore save prefs errors */ }
      }
      toast.success("✉️ Email 'Polizza pronta' inviata al cliente");
      onClose(true);
    } catch (e) {
      toast.error(`Invio fallito: ${formatApiError(e)}`);
    } finally {
      setSending(false);
    }
  };

  if (!policy) return null;

  const polNum = policy.policy_number || "—";
  const clientName = policy.client_name || "Gentile cliente";
  const subject = `✅ Polizza N° ${polNum} attivata e pronta`;
  const selectedCount = includeAttachments ? selectedIds.size : 0;
  const body = `Gentile ${clientName},

siamo lieti di comunicarLe che la Sua polizza N° ${polNum} è stata completata e attivata.
${extraMessage.trim() ? `\n${extraMessage.trim()}\n` : ""}
${selectedCount > 0 ? "In allegato trova la documentazione relativa alla polizza." : ""}

Per qualsiasi domanda non esiti a contattarci.

Cordiali saluti,`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !sending) onClose(false); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="policy-ready-notice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-emerald-600" />
            Anteprima email &quot;Polizza pronta&quot;
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
            <CheckCircle2 className="inline h-3.5 w-3.5 mr-1" />
            <strong>{polNum}</strong> → <strong>{clientName}</strong> ({policy.client_email || "—"})
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Oggetto</div>
            <div className="px-3 py-1.5 rounded-md bg-slate-100 border border-slate-200 text-sm font-medium" data-testid="ready-notice-subject">{subject}</div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Corpo email (anteprima)</div>
            <pre className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-700 whitespace-pre-wrap font-sans max-h-40 overflow-y-auto" data-testid="ready-notice-body">{body}</pre>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 flex items-center justify-between">
              <span>Messaggio aggiuntivo (opzionale)</span>
              <span className="text-[10px] text-slate-400 normal-case font-normal">{extraMessage.length}/500</span>
            </label>
            <textarea
              data-testid="ready-notice-extra-msg"
              value={extraMessage}
              onChange={(e) => setExtraMessage(e.target.value.slice(0, 500))}
              placeholder="Es. La polizza decorre dal… / Le invieremo a parte la quietanza di pagamento…"
              className="w-full text-xs px-3 py-2 rounded-md border border-slate-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-200 outline-none resize-y min-h-[60px]"
            />
          </div>

          {/* 🆕 Toggle invia allegati via email */}
          <div className="rounded-md border border-slate-200 bg-white px-3 py-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="ready-notice-include-atts"
              data-testid="ready-notice-include-attachments"
              checked={includeAttachments}
              onChange={(e) => setIncludeAttachments(e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="ready-notice-include-atts" className="text-xs text-slate-700 cursor-pointer flex items-center gap-1">
              <PaperclipIcon className="h-3.5 w-3.5" />
              <strong>Invia allegati via email</strong>
              <span className="text-slate-400">(deselezionabili singolarmente sotto)</span>
            </label>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 flex items-center justify-between gap-1">
              <span className="flex items-center gap-1">
                <Paperclip className="h-3 w-3" />
                Allegati visibili al cliente ({loadingAtts ? "…" : `${selectedIds.size}/${atts.length} selezionati`})
              </span>
              {includeAttachments && atts.length > 0 && (
                <span className="flex gap-2 normal-case font-normal">
                  <button type="button" onClick={selectAll} data-testid="ready-notice-att-select-all" className="text-[10px] text-blue-600 hover:underline">Seleziona tutti</button>
                  <button type="button" onClick={deselectAll} data-testid="ready-notice-att-deselect-all" className="text-[10px] text-slate-500 hover:underline">Deseleziona</button>
                </span>
              )}
            </div>
            <div className={`px-3 py-2 rounded-md border text-xs max-h-40 overflow-y-auto ${includeAttachments ? "bg-slate-50 border-slate-200" : "bg-slate-100 border-slate-200 opacity-60"}`} data-testid="ready-notice-attachments">
              {loadingAtts ? (
                <span className="text-slate-400">Caricamento…</span>
              ) : atts.length === 0 ? (
                <span className="text-amber-700">⚠️ Nessun allegato visibile al cliente disponibile.</span>
              ) : (
                <ul className="space-y-1">
                  {atts.map((a) => {
                    const checked = selectedIds.has(a.id);
                    return (
                      <li key={a.id} className="flex items-center gap-2" data-testid={`ready-notice-att-${a.id}`}>
                        <input
                          type="checkbox"
                          data-testid={`ready-notice-att-check-${a.id}`}
                          checked={checked}
                          disabled={!includeAttachments}
                          onChange={() => toggleAtt(a.id)}
                          className="h-3.5 w-3.5"
                        />
                        <FileText className="h-3 w-3 text-slate-500 shrink-0" />
                        <span className={`truncate flex-1 ${checked && includeAttachments ? "text-slate-800" : "text-slate-400 line-through"}`}>{a.original_filename || a.filename || "allegato"}</span>
                        {typeof a.size === "number" && (
                          <span className="text-[10px] text-slate-400 shrink-0">({Math.round(a.size / 1024)} KB)</span>
                        )}
                        <button
                          type="button"
                          data-testid={`ready-notice-att-preview-${a.id}`}
                          title="Anteprima"
                          onClick={() => previewAtt(a)}
                          disabled={previewingId === a.id}
                          className="p-1 rounded hover:bg-blue-50 text-blue-600 disabled:opacity-50 shrink-0"
                        >
                          {previewingId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* 🆕 Preferenze ricorrenti per compagnia */}
          {policy.company_id && (
            <div className="rounded-md border border-slate-200 bg-white px-3 py-2 flex items-center gap-2">
              <input
                type="checkbox"
                id="ready-notice-remember-prefs"
                data-testid="ready-notice-remember-prefs"
                checked={rememberPrefs}
                onChange={(e) => setRememberPrefs(e.target.checked)}
                disabled={!prefsLoaded}
                className="h-4 w-4"
              />
              <label htmlFor="ready-notice-remember-prefs" className="text-xs text-slate-700 cursor-pointer">
                <strong>Ricorda selezione per questa compagnia</strong>
                <span className="text-slate-400 block text-[10px]">
                  Al prossimo invio &quot;Polizza pronta&quot; per questa compagnia i file selezionati verranno pre-spuntati automaticamente.
                </span>
              </label>
            </div>
          )}

          {/* 🆕 Riepilogo prima del pulsante invia */}
          <div
            data-testid="ready-notice-summary"
            className={`rounded-md px-3 py-2 text-xs border ${selectedCount > 0 ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}
          >
            {!includeAttachments ? (
              <><strong>Invio senza allegati.</strong> L&apos;email verrà inviata al cliente senza file allegati.</>
            ) : selectedCount === 0 ? (
              <><strong>Nessun allegato selezionato.</strong> L&apos;email verrà inviata senza file allegati.</>
            ) : (
              <><strong>{selectedCount} allegat{selectedCount === 1 ? "o" : "i"}</strong> {selectedCount === 1 ? "verrà inviato" : "verranno inviati"} via email al cliente.</>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} disabled={sending} data-testid="ready-notice-cancel">
            Annulla
          </Button>
          <Button onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ready-notice-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
            {sending ? "Invio…" : "Invia al cliente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
